import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { MaterialForm } from "./MaterialForm";
import type { MaterialFormData } from "../../../shared/schemas/material.schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package } from "lucide-react";
import { ModalBreadcrumbHeader } from "@/components/shared/ModalBreadcrumbHeader";

export interface MaterialFormModalProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  initialData?: Partial<MaterialFormData> & { name?: string; vendorId?: string };
  lockedFields?: string[];
  params?: { vendorId?: string; lockedFields?: string[] };
  breadcrumb?: string[];
}

export default function MaterialFormModal({
  open,
  onClose,
  onSuccess,
  onCancel,
  initialData,
  lockedFields,
  params,
  breadcrumb,
}: MaterialFormModalProps) {
  const queryClient = useQueryClient();

  const handleClose = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const effectiveVendorId = initialData?.vendorId || params?.vendorId;
  const effectiveLockedFields = lockedFields || params?.lockedFields || (effectiveVendorId ? ["vendorId"] : []);

  const handleSubmit = async (data: any) => {
    try {
      const result = await trpcClient.materials.create.mutate(data);

      await queryClient.invalidateQueries({ queryKey: ["materials"] });
      await queryClient.invalidateQueries({ queryKey: ["combobox"] });
      await queryClient.invalidateQueries({ queryKey: ["material-lookup"] });
      await queryClient.invalidateQueries({ queryKey: ["materials-all-check"] });

      const label = result.name || result.materialId || result.code || "";
      toast.success(`Đã thêm vật tư: ${label}`);

      if (onSuccess) {
        onSuccess({
          id: result.id,
          label,
          original: {
            ...result,
            code: result.materialId,
          },
        });
      }
      if (onClose) onClose();
    } catch (err: any) {
      toast.error(err?.message || "Tạo vật tư thất bại");
    }
  };

  const effectiveBreadcrumbs = breadcrumb || ["Sản phẩm", "Vật tư"];

  const content = (
    <div className="w-full max-h-[82vh] overflow-y-auto px-1 py-1">
      <MaterialForm
        defaultValues={{
          name: initialData?.name || "",
          vendorId: effectiveVendorId || "",
          ...(initialData || {}),
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        submitLabel="Tạo & Chọn ngay"
        lockedFields={effectiveLockedFields}
        breadcrumb={effectiveBreadcrumbs}
        isModal
      />
    </div>
  );

  // When used as standalone modal with explicit open/onClose
  if (open !== undefined && onClose !== undefined) {
    return (
      <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
        <DialogContent 
          className="w-[95vw] sm:max-w-4xl md:max-w-5xl lg:max-w-5xl xl:max-w-6xl max-h-[92vh] flex flex-col p-4 sm:p-6"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
            }
          }}
        >
          <ModalBreadcrumbHeader
            breadcrumbs={effectiveBreadcrumbs}
            title="Thêm mới vật tư"
            description="Khai báo thông số kỹ thuật, định lượng và phân loại vật tư mới."
            icon={Package}
            iconColor="text-indigo-600"
          />
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  // When embedded in ComboboxFK
  return content;
}
